import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Link } from 'react-router'

import lang from '@/lang'
import ButtonSquare from '@/modules/base/components/buttons/square/button-square'
import Loader from './loader'
import { ROUTES } from '@/modules/account/constants'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    height: '100%',
    padding: 24,
    textAlign: 'center',
  },
  button: {
    marginTop: 8,
  },
}))

interface TimeoutLoaderProps {
  /** after this many ms the loader is replaced by an actionable error panel. 0 shows it immediately */
  timeoutMs: number
  /** headline of the error panel, defaults to the generic error boundary wording */
  title?: string
  /** explanation shown in the error panel, defaults to the generic error boundary wording */
  description?: string
  text?: string
  customClass?: string
  className?: string
}

/**
 * A loader that can never be terminal: if the awaited work does not finish within timeoutMs,
 * it turns into an error panel with a way out instead of spinning forever. Pass timeoutMs = 0 to
 * render that panel right away for a failure the caller already detected and reported.
 */
const TimeoutLoader: React.FC<TimeoutLoaderProps> = ({ timeoutMs, title, description, ...loaderProps }) => {
  const classes = useStyles()
  const [hasTimedOut, setHasTimedOut] = useState(timeoutMs === 0)

  useEffect(() => {
    // also covers timeoutMs switching to 0 while mounted: a caller that renders this component in
    // both its loading and its failure branch keeps the same element type, so react reuses this
    // instance instead of remounting it and the initial state above would never be re-evaluated
    if (timeoutMs === 0) {
      setHasTimedOut(true)
      return undefined
    }

    const timer = setTimeout(() => setHasTimedOut(true), timeoutMs)
    return () => clearTimeout(timer)
  }, [timeoutMs])

  useEffect(() => {
    // only report an actual timeout - with timeoutMs = 0 the caller has already reported the
    // failure under a meaningful label, so reporting again would just add a misleading entry
    if (!hasTimedOut || timeoutMs === 0) return

    window.elasticApm?.captureError?.(new Error(`loader-timeout:${window.location.pathname}`))
  }, [hasTimedOut, timeoutMs])

  if (!hasTimedOut) {
    return <Loader {...loaderProps} />
  }

  return (
    <Box className={classes.container} data-qs-id="timeout-loader-error">
      <Typography variant="h6">{title ?? lang.errorBoundary.title}</Typography>
      <Typography>{description ?? lang.errorBoundary.description}</Typography>
      <ButtonSquare
        dataQsId="timeout-loader-reload"
        variant="small"
        onClick={() => window.location.reload()}
        label={lang.errorBoundary.reloadButton}
        className={classes.button}
      />
      <Link to={ROUTES.login}>{lang.loginButtonLabel}</Link>
    </Box>
  )
}

export default TimeoutLoader
